import { useCallback, useEffect, useRef, useState } from 'react';

import { CONNECT_HANDOFF_GRACE_MS, connectHandoffPending } from './connect-handoff';

/**
 * Local busy state for the window after a connector connects, before the turn the genome starts is
 * visible on the conversation (see `lib/connect-handoff`).
 *
 * `noteConnected` is handed to the connector card, which calls it on the offered/waiting → connected
 * transition. The flag expires on its own; a real turn taking over (`remoteTurnActive`, a streamed
 * THINKING row) simply outranks it at the render site, so nothing has to clear it.
 *
 * `latestReplyKey` is the newest assistant reply with content (`latestReplyKey(messages)`). The card
 * that armed the handoff rides one of those, so "the last row is a reply with content" is true the
 * whole time the handoff is bridging — it cannot be what hides the loader. The flag instead ends the
 * moment a DIFFERENT reply appears: that one is the turn the connect woke.
 */
export function useConnectHandoff(latestReplyKey: string | undefined): { pending: boolean; noteConnected: () => void } {
  const [armedAt, setArmedAt] = useState<number | null>(null);
  const [armedReplyKey, setArmedReplyKey] = useState<string | undefined>(undefined);

  const latestReplyKeyRef = useRef(latestReplyKey);
  latestReplyKeyRef.current = latestReplyKey;

  const noteConnected = useCallback(() => {
    setArmedAt(Date.now());
    setArmedReplyKey(latestReplyKeyRef.current);
  }, []);

  // Re-evaluate once at the horizon — the flag has no other end, so without this the indicator it
  // switched on would never switch off.
  useEffect(() => {
    if (armedAt === null) return undefined;
    const timer = setTimeout(() => setArmedAt(null), CONNECT_HANDOFF_GRACE_MS);
    return () => clearTimeout(timer);
  }, [armedAt]);

  return { pending: connectHandoffPending(armedAt) && armedReplyKey === latestReplyKey, noteConnected };
}
